/**
 * Owner / admin inbox for walkthrough access requests.
 * Approve issues a tokenized link (copy-to-clipboard) + audit-logs.
 * Decline records a reason and notifies (audit only).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, Copy, Check, RefreshCw, Shield, Clock } from 'lucide-react';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function StatusPill({ status }) {
  const tone = {
    pending: { bg: 'bg-amber-100', fg: 'text-amber-800', border: 'border-amber-300' },
    approved: { bg: 'bg-emerald-100', fg: 'text-emerald-800', border: 'border-emerald-300' },
    declined: { bg: 'bg-rose-100', fg: 'text-rose-800', border: 'border-rose-300' },
  }[status] || { bg: 'bg-slate-100', fg: 'text-slate-800', border: 'border-slate-300' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${tone.bg} ${tone.fg} ${tone.border}`}>{status}</span>
  );
}

function Row({ row, onAction, busyId, lastApproval }) {
  const [expanded, setExpanded] = useState(row.status === 'pending');
  const [expiryDays, setExpiryDays] = useState(14);
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);
  const approvalForThis = lastApproval && lastApproval.id === row.id ? lastApproval : null;

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      logger.warn('clipboard copy failed', e?.message);
    }
  };

  return (
    <li className="rounded-xl border border-[var(--vh-border)] bg-[var(--vh-bg-surface)]" data-testid={`walkthrough-request-${row.id}`}>
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full text-left p-4 flex flex-wrap items-start justify-between gap-3"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-[var(--vh-text-primary)]">{row.full_name}</p>
            <StatusPill status={row.status} />
            {row.status === 'approved' && row.view_count > 0 && (
              <span className="text-[10px] text-[var(--vh-text-muted)]">· {row.view_count} view{row.view_count > 1 ? 's' : ''}</span>
            )}
          </div>
          <p className="text-xs text-[var(--vh-text-muted)] mt-0.5">{row.email} · {row.organisation} · {row.role}</p>
          <p className="text-xs text-[var(--vh-text-muted)] mt-0.5">Requested {fmt(row.created_at)}</p>
        </div>
        <div className="text-xs text-[var(--vh-text-muted)] shrink-0">{expanded ? '−' : '+'}</div>
      </button>
      {expanded && (
        <div className="border-t border-[var(--vh-border)] p-4 bg-[var(--vh-bg-muted)] space-y-3">
          <p className="text-sm text-[var(--vh-text-primary)] whitespace-pre-wrap">{row.purpose}</p>
          <p className="text-xs text-[var(--vh-text-muted)]">
            <Shield className="inline w-3 h-3 mr-1" />
            NDA {row.nda_version} accepted on {fmt(row.nda_accepted_at)}
          </p>

          {row.status === 'pending' && (
            <div className="flex flex-wrap items-end gap-3 pt-2">
              <label className="text-xs text-[var(--vh-text-secondary)]">
                Expires in
                <select value={expiryDays} onChange={(e) => setExpiryDays(Number(e.target.value))} className="ml-2 px-2 py-1 border border-[var(--vh-border)] rounded bg-[var(--vh-bg-surface)] text-sm">
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional internal note"
                className="flex-1 min-w-[200px] px-3 py-1.5 border border-[var(--vh-border)] rounded bg-[var(--vh-bg-surface)] text-sm"
              />
              <button
                type="button"
                onClick={() => onAction(row.id, 'approve', { expiry_days: expiryDays, note })}
                disabled={busyId === row.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white text-xs font-medium disabled:opacity-60"
                data-testid={`walkthrough-approve-${row.id}`}
              >
                {busyId === row.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Approve
              </button>
              <button
                type="button"
                onClick={() => onAction(row.id, 'decline', { expiry_days: 1, note })}
                disabled={busyId === row.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--vh-border)] text-[var(--vh-text-secondary)] hover:bg-[var(--vh-bg-surface)] text-xs font-medium disabled:opacity-60"
                data-testid={`walkthrough-decline-${row.id}`}
              >
                <XCircle className="w-3.5 h-3.5" />
                Decline
              </button>
            </div>
          )}

          {row.status === 'approved' && (
            <div className="text-xs text-[var(--vh-text-secondary)] flex flex-wrap items-center gap-2 pt-1">
              <Clock className="w-3.5 h-3.5" />
              <span>Expires {fmt(row.expires_at)}</span>
              {row.last_viewed_at && <span>· Last viewed {fmt(row.last_viewed_at)}</span>}
            </div>
          )}

          {approvalForThis && approvalForThis.share_url && (
            <div className="p-3 rounded-lg border border-emerald-300 bg-emerald-50 text-xs text-emerald-900 mt-2">
              <p className="font-medium">Share link generated — copy it now and email it to {approvalForThis.email_to}:</p>
              <div className="flex items-stretch gap-2 mt-2">
                <input readOnly value={approvalForThis.share_url} className="flex-1 min-w-0 px-2 py-1.5 border border-emerald-300 rounded bg-white text-xs" />
                <button type="button" onClick={() => copy(approvalForThis.share_url)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-800 text-white text-xs">
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export default function WalkthroughInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [lastApproval, setLastApproval] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/walkthrough/access-requests`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load walkthrough access requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action, body) => {
    setBusyId(id);
    setError('');
    try {
      const { data } = await axios.post(`${API}/walkthrough/access-requests/${id}/${action}`, body);
      if (action === 'approve' && data?.share_url) {
        setLastApproval({ id, ...data });
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Action failed.');
    } finally {
      setBusyId(null);
    }
  };

  const pending = rows.filter(r => r.status === 'pending');
  const history = rows.filter(r => r.status !== 'pending');

  return (
    <section className="rounded-2xl bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] p-5" data-testid="walkthrough-inbox">
      <header className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-xl text-[var(--vh-text-primary)]">Walkthrough access requests</h2>
          <p className="text-xs text-[var(--vh-text-muted)] mt-0.5">External viewers asking to see the confidential walkthrough. Every viewer accepts the NDA and every view is audit-logged.</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[var(--vh-border)] text-xs text-[var(--vh-text-secondary)] hover:bg-[var(--vh-bg-muted)] disabled:opacity-60"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </header>

      {error && <p className="text-sm text-red-700 mb-2">{error}</p>}

      <div className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-[var(--vh-text-muted)] mb-2">Pending · {pending.length}</p>
          {pending.length === 0 ? (
            <p className="text-sm text-[var(--vh-text-muted)]">No pending requests.</p>
          ) : (
            <ul className="space-y-2">
              {pending.map(r => <Row key={r.id} row={r} onAction={handleAction} busyId={busyId} lastApproval={lastApproval} />)}
            </ul>
          )}
        </div>

        {history.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-widest text-[var(--vh-text-muted)] mb-2 mt-4">History · {history.length}</p>
            <ul className="space-y-2">
              {history.map(r => <Row key={r.id} row={r} onAction={handleAction} busyId={busyId} lastApproval={lastApproval} />)}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
